import { BaseApiService } from './BaseApiService';

export interface FilterOption {
  id: string;
  name: string;
}

export interface ComparisonSummary {
  revenue: number;
  qty: number;
  active_customers: number;
  orders: number;
}

export interface ComparisonRow {
  row_key: string;
  row_label: string;
  revenue: number;
  qty: number;
  avg_sale: number;
  min_sale: number;
  max_sale: number;
  avg_price: number;
  min_price: number;
  max_price: number;
  /** Customer sales of every product in the date range. Set on product adoption rows. */
  customer_total_sales?: number;
  /** Distinct invoices for that customer in the date range. Set on product adoption rows. */
  customer_order_count?: number;
}

export function comparison_filters_without_product(
  filters: ComparisonFilters
): ComparisonFilters {
  const next: ComparisonFilters = { ...filters };
  delete next.products;
  delete next.product_ids;
  return next;
}

export interface ComparisonWidget {
  group_key: string;
  group_label: string;
  rows: ComparisonRow[];
}

export interface ComparisonLineItem {
  date: string;
  invoice_number: string;
  customer_number: string;
  customer_name: string;
  item_number: string;
  item_name: string;
  qty: number;
  revenue: number;
  unit_price: number;
}

export interface ComparisonDataResult {
  widgets: ComparisonWidget[];
  truncated: boolean;
  has_more: boolean;
  group_count: number;
}

export type ProductCustomerStatus = 'bought' | 'not_bought' | 'both';

export interface ProductCustomerCursor {
  after_key?: string;
  after_label?: string;
  after_revenue?: number;
}

export interface ProductCustomerAdoptionResult {
  rows: ComparisonRow[];
  bought_rows: ComparisonRow[];
  not_bought_rows: ComparisonRow[];
  has_more: boolean;
  truncated: boolean;
  bought_count: number;
  not_bought_count: number;
  universe_count: number;
  status: ProductCustomerStatus;
}

export const ADOPTION_PAGE_SIZE = 100;
export const ADOPTION_MAX_LIMIT = 5000;

export type GroupByDimension =
  | 'product'
  | 'customer'
  | 'brand'
  | 'category'
  | 'sales_rep'
  | 'territory';

export interface ComparisonFilters {
  brands?: string[];
  categories?: string[];
  products?: string[];
  /** Numeric product IDs (executive dashboard / internal filters). */
  product_ids?: number[];
  customers?: string[];
  sales_reps?: string[];
  territories?: string[];
  start_date?: string;
  end_date?: string;
}

class ComparisonDashboardService extends BaseApiService {
  private basePath = '/reports/comparison-dashboard';

  private filtersToParams(filters: ComparisonFilters): Record<string, any> {
    const params: Record<string, any> = {};
    if (filters.brands?.length) params.brands = filters.brands.join(',');
    if (filters.categories?.length) params.categories = filters.categories.join(',');
    if (filters.products?.length) params.products = filters.products.join(',');
    if (filters.product_ids?.length) params.product_ids = filters.product_ids.join(',');
    if (filters.customers?.length) params.customers = filters.customers.join(',');
    if (filters.sales_reps?.length) params.sales_reps = filters.sales_reps.join(',');
    if (filters.territories?.length) params.territories = filters.territories.join(',');
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    return params;
  }

  async getFilterOptions(dimension: string, search: string): Promise<FilterOption[]> {
    const response = await this.get<{ success: boolean; data: FilterOption[] }>(
      `${this.basePath}/filter-options`,
      { dimension, search }
    );
    return response.data;
  }

  async getSummary(filters: ComparisonFilters): Promise<ComparisonSummary> {
    const params = this.filtersToParams(filters);
    const response = await this.get<{ success: boolean; data: ComparisonSummary }>(
      `${this.basePath}/summary`,
      params
    );
    return response.data;
  }

  async getComparisonData(
    filters: ComparisonFilters,
    group_by: GroupByDimension,
    offset: number = 0,
    limit?: number
  ): Promise<ComparisonDataResult> {
    const params: Record<string, any> = { ...this.filtersToParams(filters), group_by, offset };
    if (limit !== undefined) params.limit = limit;
    const response = await this.get<{
      success: boolean;
      data: ComparisonDataResult | ComparisonWidget[];
    }>(`${this.basePath}/data`, params);
    if (Array.isArray(response.data)) {
      return {
        widgets: response.data,
        truncated: false,
        has_more: false,
        group_count: response.data.length,
      };
    }
    return {
      ...response.data,
      has_more: Boolean(response.data.has_more),
    };
  }

  async getProductCustomers(
    filters: ComparisonFilters,
    product_key: string,
    status: ProductCustomerStatus,
    limit?: number,
    cursor?: ProductCustomerCursor
  ): Promise<ProductCustomerAdoptionResult> {
    const params: Record<string, any> = {
      ...this.filtersToParams(filters),
      product_key,
      status,
    };
    if (limit !== undefined) params.limit = limit;
    if (cursor?.after_key) {
      params.after_key = cursor.after_key;
      params.after_label = cursor.after_label ?? '';
      if (cursor.after_revenue !== undefined) params.after_revenue = cursor.after_revenue;
    }
    const response = await this.get<{
      success: boolean;
      data: ProductCustomerAdoptionResult;
    }>(`${this.basePath}/product-customers`, params);
    return response.data;
  }

  async getLineItems(
    filters: ComparisonFilters,
    group_by: GroupByDimension,
    group_key: string,
    row_key?: string
  ): Promise<{ rows: ComparisonLineItem[]; truncated: boolean }> {
    const params: Record<string, any> = {
      ...this.filtersToParams(filters),
      group_by,
      group_key,
    };
    if (row_key) params.row_key = row_key;
    const response = await this.get<{
      success: boolean;
      data: { rows: ComparisonLineItem[]; truncated: boolean };
    }>(`${this.basePath}/line-items`, params);
    return response.data;
  }
}

export const comparisonDashboardService = new ComparisonDashboardService();
